import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import {
  InfoBoxWrapper, MapControl,
  CensusTractSummaryOverlay, CensusTractLegendItem,
  MapControlButton, MapControlButtonGroup, MapControlSelection, MapDrawing
} from 'components/map';
import { MarkerWrapper, CensusTractOverlay } from 'components/underwrite/shared/map';
import { withStyles } from '@material-ui/core/styles';
import {
  setSelectedCT, setCTMouseoverData, setCTLegendData, setSelectedCompDataType,
  setShowCTMouseoverData, setPropertyUWMapState, setPropertyUWMarkerState, toggleIsSelectedAsComp
} from 'store/actions';
import {
  isUsersValuationSelector,
  filteredSelectedOpportunityCompsSelector
} from 'store/selectors';
// Marker clusterer images
import debounce from 'lodash/debounce';
import ImageSearchIcon from '@material-ui/icons/ImageSearch';
import GestureIcon from '@material-ui/icons/Gesture';
import MapIcon from '@material-ui/icons/Map';
import TerrainIcon from '@material-ui/icons/Terrain';
import InfoIcon from '@material-ui/icons/Info';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import { Constants } from 'utilities/constants';
import { formatValue } from "utilities/format";

const PropertyUWMap = ({
  map, autoNav, setPropertyUWMapState, gestureHandling, mapViewType, showCTMouseoverData, setShowCTMouseoverData,
  selectedOpportunity, selectedOpportunityComps, setPropertyUWMarkerState, selectedMarker, classes,
  selectedMarkerLabel, selectedMarkerImages, isFetchingImages, images, propertyGridCurrentViewData,
  isUsersValuation, isReadonlyMode, isInfoBoxOpen, selectedCompDataType, selectedCT, setSelectedCT,
  ctMouseoverData, ctLegendData, propertyUWMarkerState, setSelectedCompDataType, toggleIsSelectedAsComp,
  setCTMouseoverData, propertyUWMapState, isDrawing, polygon, isStreetView, isFetchingPropertyUWData, setCTLegendData
}) => {
  const [allowDrawing, setAllowDrawing] = useState(true);

  // If auto nav is on and selected marker changed by click or grid row selection, pan to marker
  useEffect(() => {
    if (autoNav && selectedMarker) {
      if (selectedMarker.latitude && selectedMarker.longitude) {
        map.panTo(new window.google.maps.LatLng(selectedMarker.latitude, selectedMarker.longitude));
      } else {
        const selectedOpp = selectedOpportunityComps.find((el) => el.id === selectedMarker.id);
        if (selectedOpp.latitude && selectedOpp.longitude) {
          map.panTo(new window.google.maps.LatLng(selectedOpp.latitude, selectedOpp.longitude));
        }
      }
    }
  }, [selectedMarker]);

  useEffect(() => { 
    if (isFetchingPropertyUWData) return;
    if (selectedOpportunityComps.length) { // Set map bounds based on comp coordinates
      if (propertyUWMapState.isDrawing) setPropertyUWMapState({ isDrawing: false });
      map.getStreetView().setVisible(false);
      setDefaultBounds();
      if (ctMouseoverData) setCTMouseoverData(null);
    } else if (selectedOpportunity?.latitude && selectedOpportunity?.longitude) { // If no comps found or comps still fetching, set zoom/position based on selectedOpportunity

        map.setZoom(18);
        map.panTo(new window.google.maps.LatLng(selectedOpportunity.latitude, selectedOpportunity.longitude));
        if (ctMouseoverData) setCTMouseoverData(null);

    }
  }, [selectedOpportunityComps, isFetchingPropertyUWData]); // Only changes on first opportunity load (empty array to array of comps) or on opportunity change

  const mapDataTypeOptions = [
    { onClick: () => setMapDataType('rent'), text: 'Rent', active: selectedCompDataType === 'rent'},
    { onClick: () => setMapDataType('sale'), text: 'For Sale', active: selectedCompDataType === 'sale' },
    { onClick: () => setMapDataType('sold'), text: 'Sold', active: selectedCompDataType === 'sold' }
  ];
  const rentLabelOptions = [
    { value: '', label: '' },
    { value: 'yearBuilt', label: 'Year Built' },
    { value: 'beds', label: 'Beds' },
    { value: 'baths', label: 'Baths' },
    { value: 'sqft', label: 'Sqft' },
    { value: 'rentprice', label: 'Rent' },            // keep the value the same between rent and saleSold labels
    { value: 'rentpricesqft', label: 'Rent/Sqft' }
  ];
  const saleSoldLabelOptions = [
    { value: '', label: '' },
    { value: 'yearBuilt', label: 'Year Built' },
    { value: 'beds', label: 'Beds' },
    { value: 'baths', label: 'Baths' },
    { value: 'sqft', label: 'Sqft' },
    { value: 'rentprice', label: 'Price' },
    { value: 'rentpricesqft', label: 'Price/Sqft' }
  ];
  const censustractOptions = [
    { value: '', label: '' },
    { value: 'population', label: 'Population' },
    { value: 'owner', label: 'Owner %' },
    { value: 'rentPct', label: 'Rent %' },
    { value: 'vacant', label: 'Vacant %' },
    { value: 'rent', label: 'Rent Price' },
    { value: 'medianHomeValue', label: 'Median Home Value' },
    // { value: 'crime', label: 'Crime Index' },
    // { value: 'wealth', label: 'Wealth' },
    { value: 'hpa6mo', label: 'HPA 6 Mo' },
    { value: 'amherstEstimatedAMI', label: 'AMI' },
    { value: 'affordablePmt', label: 'Affordable Pmt' },
    { value: 'affordabilityPrice', label: 'Affordability Price' },
    { value: 'affordabilityScore', label: 'Affordability Score' },
  ];


  const setDefaultBounds = () => {
    const bounds = new window.google.maps.LatLngBounds();
    let shouldSetBounds = false;
    if (selectedOpportunityComps) {
      selectedOpportunityComps.forEach(bound => {
        if (bound.latitude && bound.longitude) {
          let position = new window.google.maps.LatLng(bound.latitude, bound.longitude);
          bounds.extend(position);
        }
      });
      shouldSetBounds = true;
    }
    if (selectedOpportunity && selectedOpportunity.latitude && selectedOpportunity.longitude) {
      bounds.extend(new window.google.maps.LatLng(selectedOpportunity.latitude, selectedOpportunity.longitude));
      shouldSetBounds = true;
    }
    if (shouldSetBounds) {
      map.fitBounds(bounds, 40);     // This should set the zoom level to ensure all markers are inside the viewport
    }
  }

  const toggleDrawIsOn = () => {
    setUncapturedMarkers([]);
    setPropertyUWMapState({ isDrawing: !isDrawing });
  }

  const toggleGestureHandling = () => {
    let newGestureHandling = gestureHandling === 'cooperative' ? 'auto' : 'cooperative';
    setPropertyUWMapState({ gestureHandling: newGestureHandling });
    map.setOptions({ gestureHandling: newGestureHandling });
  }

  const toggleShowCTMouseoverData = () => {
    setShowCTMouseoverData(!showCTMouseoverData);
  }

  const toggleMapViewType = () => {
    let newMapViewType = mapViewType === 'roadmap' ? 'hybrid' : 'roadmap';
    setPropertyUWMapState({ mapViewType: newMapViewType });
    map.setOptions({ mapTypeId: newMapViewType });
  }

  const toggleAutoNav = () => {
    setPropertyUWMapState({ autoNav: !autoNav });
  }

  const setSelectedMarker = marker => {
    setPropertyUWMarkerState({ isInfoBoxOpen: true, isFetchingImages: true, selectedMarkerImages: [], selectedMarker: marker });
    // setPropertyUWMarkerState({ isInfoBoxOpen: true, isFetchingImages: false, selectedMarkerImages: [], selectedMarker: marker });
  }

  const setUncapturedMarkers = markersOutside => {
    setPropertyUWMarkerState({ uncapturedMarkers: markersOutside });
  }

  const setMapDataType = value => {
    if (value !== selectedCompDataType) {
      setPropertyUWMapState({ isDrawing: false });
      setPropertyUWMarkerState({
        selectedMarker: null,
        isInfoBoxOpen: false,
        isFetchingImages: false,
        selectedMarkerImages: [],
        uncapturedMarkers: []
      });
      setSelectedCompDataType(value);
    }
  }

  const setSelectedMarkerLabel = e => {
    setPropertyUWMapState({ selectedMarkerLabel: e.target.value });
  }

  const setSelectedCensusTract = e => {
    setSelectedCT(e.target.value);
  }

  const onMarkerClusterClick = markerCluster => markerCluster.getMap().fitBounds(markerCluster.getBounds());

  const onClusteringBegin = debounce(clusterer => {
    if (map.getZoom() < 10 && selectedMarker) {
      //keep selection on selected marker if it is not part of any cluster
      let isSelectedMarkerInCluster = clusterer.getClusters().filter(c => c.getMarkers().length > 1 && c.getMarkers().filter(m => selectedMarker && m.labelContent.children[0].dataset.id === selectedMarker.id.toString()).length > 0).length > 0;
      if (isSelectedMarkerInCluster) {
        setPropertyUWMarkerState({
          isInfoBoxOpen: false,
          selectedMarker: null,
          isFetchingImages: false,
          selectedMarkerImages: [],
        });
      }
    }
    if (map.getZoom() < 10) {
      if (allowDrawing) {
        let clustersExists = clusterer.getClusters().filter(c => c.getMarkers().length > 1).length > 0;
        if (clustersExists) {
          setAllowDrawing(false);
          setUncapturedMarkers([]);
          setPropertyUWMapState({ isDrawing: false });
        }
      }
    } else if (!allowDrawing) {
      setAllowDrawing(true);
    }
  }, 300);

  const getIconType = source => {
    if (source === 'MSR') {
      return 'msr';
    }
    return 'house';
  }

  const getIconColor = (source, masterCompTypeLabel)  => {
    if (source === 'MSR') {
      if (masterCompTypeLabel.toUpperCase() === 'T1') {
        return 'green';
      } else if (masterCompTypeLabel.toUpperCase() === 'T2') {
        return 'blue';
      } else if (masterCompTypeLabel.toUpperCase() === 'C') {
        return 'grey';
      }
    }
    if (masterCompTypeLabel.toUpperCase() === 'T1') {
      return '#50eb50';//green
    } else if (masterCompTypeLabel.toUpperCase() === 'T2') {
      return 'blue';
    } else if (masterCompTypeLabel.toUpperCase() === 'C') {
      return 'grey';
    }
  }

  const selectionEnabled = isUsersValuation && !isReadonlyMode;
  let markers = useMemo(() =>
    selectedOpportunityComps.map(marker => (
      <MarkerWrapper
        module={Constants.Module.UNDERWRITE}
        key={marker.id}
        marker={marker}
        map={map}
        selectedMarker={selectedMarker}
        setSelectedMarker={setSelectedMarker}
        selectedMarkerLabel={selectedMarkerLabel}
        autoNav={autoNav}
        toggleIsSelectedAsComp={toggleIsSelectedAsComp}
        uwType={'propertyUW'}
        selectedMarkerImages={selectedMarkerImages}
        isFetchingImages={isFetchingImages}
        images={images}
        gridCurrentViewData={propertyGridCurrentViewData}
        selectionEnabled={selectionEnabled}
        isCaptured={!propertyUWMarkerState.uncapturedMarkers.includes(marker.id)}
        iconColor={getIconColor(marker.source, marker.masterCompTypeLabel)}
        iconType={getIconType(marker.source)}
      />
    )), [selectedMarker, selectedMarkerLabel, isStreetView, autoNav, selectedMarkerImages, isFetchingImages, images, JSON.stringify(propertyGridCurrentViewData), selectionEnabled, JSON.stringify(selectedOpportunityComps), propertyUWMarkerState.uncapturedMarkers]);

  return (
    <>
      <>
        {/* <MarkerClusterer
          onClick={onMarkerClusterClick}
          onClusteringBegin={onClusteringBegin}
          averageCenter
          enableRetinaIcons
          gridSize={60}
          zoomOnClick={false}
          maxZoom={9}
          styles={clusterStyles}
        > */}  {/* Commented out because cluster need to be fully working with drawing, and need to fix bug about row selecting when clustering occurs*/}
          {markers}
          {selectedOpportunity &&          // Add in the subject home
            <MarkerWrapper
              module={Constants.Module.UNDERWRITE}
              key={selectedOpportunity.id}
              marker={{ ...selectedOpportunity, isSubject: true }}
              map={map}
              selectedMarker={selectedMarker}
              setSelectedMarker={setSelectedMarker}
              selectedMarkerLabel={selectedMarkerLabel}
              selectedMarkerImages={selectedMarkerImages}
              isFetchingImages={isFetchingImages}
              images={images}
              iconColor={'orange'}
              iconType={"house"}
              gridCurrentViewData={[selectedOpportunity.id]}
              selectionEnabled={selectionEnabled}
              isStreetView={isStreetView}
            />
          }
        {/* </MarkerClusterer> */}
        <MapControl map={map} position={window.google.maps.ControlPosition.TOP_LEFT} className='mapControlMargin'>
          <div className={classes.controlContainer}>
            <MapControlButtonGroup buttons={mapDataTypeOptions}/>
          </div>
        </MapControl>
        <MapControl map={map} position={window.google.maps.ControlPosition.TOP_RIGHT} className='mapControlMargin'>
          <div className={classes.controlContainer}>
            <MapControlSelection title='LABEL' id='label' value={selectedMarkerLabel}
              onChange={setSelectedMarkerLabel}
              options={(selectedCompDataType === 'sale' || selectedCompDataType === 'sold') ?
                saleSoldLabelOptions : rentLabelOptions} />
            <MapControlSelection title='CENSUS TRACT' id='censustract' value={selectedCT}
              onChange={setSelectedCensusTract} options={censustractOptions} />
            {allowDrawing && <MapControlButton title='Draw shape' active={isDrawing} onClick={toggleDrawIsOn}><GestureIcon className={classes.mapIcon}/></MapControlButton>}
            <MapControlButton title='Auto Navigate' active={autoNav} onClick={toggleAutoNav}><ImageSearchIcon className={classes.mapIcon}/></MapControlButton>
            <MapControlButton title='Toggle map view' onClick={toggleMapViewType}>{mapViewType === 'roadmap' ? <TerrainIcon className={classes.mapIcon}/> : <MapIcon className={classes.mapIcon}/>}</MapControlButton>
            <MapControlButton title='Toggle scroll zoom' onClick={toggleGestureHandling} active={gestureHandling === 'auto'}><ZoomOutMapIcon className={classes.mapIcon}/></MapControlButton>
            <MapControlButton title='Toggle Census Tract Summary' onClick={toggleShowCTMouseoverData} active={showCTMouseoverData}><InfoIcon className={classes.mapIcon}/></MapControlButton>
          </div>
        </MapControl>
        <CensusTractOverlay module={Constants.Module.UNDERWRITE} map={map}
          selectedCT={selectedCT} ctMouseoverData={ctMouseoverData}
          setCTMouseoverData={setCTMouseoverData} setCTLegendData={setCTLegendData} mapViewType={mapViewType} drawIsOn={isDrawing} />
        {showCTMouseoverData && ctMouseoverData &&
          <MapControl map={map} position={window.google.maps.ControlPosition.BOTTOM_LEFT} className='censusTractSummary'>
            <CensusTractSummaryOverlay ctMouseoverData={ctMouseoverData} />
          </MapControl>
        }
        {ctLegendData &&
          <MapControl map={map} position={window.google.maps.ControlPosition.RIGHT_TOP} className='ctLegend'>
            <div className={classes.ctLegend}>
              <CensusTractLegendItem legendColor='red' legendText={formatValue(ctLegendData.min, ctLegendData.format)} />
              <span className={classes.leftRightArrow}>↔</span>
              <CensusTractLegendItem legendColor='green' legendText={formatValue(ctLegendData.max, ctLegendData.format)} />
            </div>
          </MapControl>
        }
        <MapDrawing
          map={map}
          drawIsOn={isDrawing}
          selectedOpportunityComps={selectedOpportunityComps}
          setUncapturedMarkers={setUncapturedMarkers}
          uwType={'propertyUW'}
          polygon={polygon}
          setMapState={setPropertyUWMapState}
        />
      </>
      {selectedMarker && isInfoBoxOpen &&
        <InfoBoxWrapper
          markerState={propertyUWMarkerState}
          setMarkerState={setPropertyUWMarkerState}
        />
      }
    </>
  )
};
const styles = theme => ({
  controlContainer: {
    display: 'flex',
    //background: theme.palette.background.default,
  },
  toggle: {
    border: '1px solid #bfbfbf',
  },
  ctLegend: {
    color: 'white',
    padding: '4px 8px',
    border: '1px dotted solid',
    borderRadius: '5px',
    backgroundColor: 'black',
    opacity: '0.75',
    display: 'flex',
    alignItems: 'center'
  },
  mapIcon: {
    fontSize: '1rem'
  },
  leftRightArrow: {
    margin: '0 4px 2px',
    fontSize: '14px'
  }
});

const mapStateToProps = (state) => {
  return {
    isUsersValuation: isUsersValuationSelector(state),
    isReadonlyMode: state.propertyUWState.selectedContainer?.viewingReadonlyMode,
    propertyUWMapState: state.propertyUWState.propertyUWMapState,
    autoNav: state.propertyUWState.propertyUWMapState.autoNav,
    gestureHandling: state.propertyUWState.propertyUWMapState.gestureHandling,
    mapViewType: state.propertyUWState.propertyUWMapState.mapViewType,
    selectedMarkerLabel: state.propertyUWState.propertyUWMapState.selectedMarkerLabel,
    polygon: state.propertyUWState.propertyUWMapState.polygon,
    selectedCT: state.propertyUWState.selectedCT,
    ctMouseoverData: state.propertyUWState.ctMouseoverData,
    showCTMouseoverData: state.propertyUWState.showCTMouseoverData,
    ctLegendData: state.propertyUWState.ctLegendData,
    images: state.propertyUWState.images,
    propertyGridCurrentViewData: state.propertyUWState.propertyGridCurrentViewData,
    propertyUWMarkerState: state.propertyUWState.propertyUWMarkerState,
    selectedMarkerImages: state.propertyUWState.propertyUWMarkerState.selectedMarkerImages,
    selectedMarker: state.propertyUWState.propertyUWMarkerState.selectedMarker,
    isFetchingImages: state.propertyUWState.propertyUWMarkerState.isFetchingImages,
    isInfoBoxOpen: state.propertyUWState.propertyUWMarkerState.isInfoBoxOpen,
    selectedOpportunityComps: filteredSelectedOpportunityCompsSelector(state),
    selectedOpportunity: state.propertyUWState.propertyUWClientTxnOpp,
    selectedCompDataType: state.propertyUWState.selectedCompDataType,
    isDrawing: state.propertyUWState.propertyUWMapState.isDrawing,
    isFetchingPropertyUWData: state.propertyUWState.isFetchingPropertyUWData
  }
};

const mapDispatchToProps = (dispatch) => ({
  setSelectedCompDataType: mapDataType => dispatch(setSelectedCompDataType(mapDataType)),
  setSelectedCT: ct => dispatch(setSelectedCT(ct)),
  setCTMouseoverData: data => dispatch(setCTMouseoverData(data)),
  setShowCTMouseoverData: show => dispatch(setShowCTMouseoverData(show)),
  setCTLegendData: data => dispatch(setCTLegendData(data)),
  setPropertyUWMapState: propertyUWMapState => dispatch(setPropertyUWMapState(propertyUWMapState)),
  toggleIsSelectedAsComp: (compId) => dispatch(toggleIsSelectedAsComp(compId)),
  setPropertyUWMarkerState: propertyUWMarkerState => dispatch(setPropertyUWMarkerState(propertyUWMarkerState))
});

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PropertyUWMap);